import type {
  PlatformId,
  StrategyPlatformCadence,
  StrategyPlatformCadenceSlot,
} from '../types';
import type { AuthorPlatformLite } from '../hooks/useAuthorPlatforms';
import { DEFAULT_PLATFORM_WINDOW } from './assignTimeSlots';

export const CADENCE_DAY_CODES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export type CadenceDayCode = (typeof CADENCE_DAY_CODES)[number];
export const MAX_CADENCE_SLOTS_PER_WEEK = 7;

export interface CadenceFieldSlot {
  day: CadenceDayCode;
  hour: number;
  minute?: number;
  score?: number;
}

export interface PlatformOptimalSlot {
  day: CadenceDayCode;
  /** "HH:mm" 24h local time. */
  timeLocal: string;
  score?: number;
}

const DAY_TO_INDEX = CADENCE_DAY_CODES.reduce<Record<string, number>>((acc, day, index) => {
  acc[day.toLowerCase()] = index;
  return acc;
}, {});

export function cadenceDayToIndex(day: string | number | undefined): number | null {
  if (typeof day === 'number' && Number.isInteger(day) && day >= 0 && day <= 6) return day;
  if (typeof day !== 'string') return null;
  const normalized = day.trim().slice(0, 3).toLowerCase();
  return DAY_TO_INDEX[normalized] ?? null;
}

export function cadenceIndexToDay(index: number): CadenceDayCode {
  return CADENCE_DAY_CODES[Math.max(0, Math.min(6, index))] ?? 'Mon';
}

export function formatHourLocal(hour: number): string {
  return formatTimeLocal(hour, 0);
}

export function formatTimeLocal(hour: number, minute = 0): string {
  const bounded = Math.max(0, Math.min(23, Number.isFinite(hour) ? Math.round(hour) : 9));
  const boundedMinute = Math.max(0, Math.min(59, Number.isFinite(minute) ? Math.round(minute) : 0));
  return `${String(bounded).padStart(2, '0')}:${String(boundedMinute).padStart(2, '0')}`;
}

export function parseTimeLocal(value: string | undefined): { hour: number; minute: number } {
  const [hh, mm] = String(value ?? '').split(':').map(Number);
  return {
    hour: Number.isFinite(hh) ? Math.max(0, Math.min(23, hh)) : 9,
    minute: Number.isFinite(mm) ? Math.max(0, Math.min(59, mm)) : 0,
  };
}

export function parseHourLocal(value: string | undefined): number {
  return parseTimeLocal(value).hour;
}

export function slotsToCadenceFieldSlots(
  slots: StrategyPlatformCadenceSlot[] | undefined,
): CadenceFieldSlot[] {
  return (slots ?? []).slice(0, MAX_CADENCE_SLOTS_PER_WEEK).map((slot) => {
    const parsedTime = parseTimeLocal(slot.timeLocal);
    return {
      day: cadenceIndexToDay(slot.weekdayIndex),
      hour: parsedTime.hour,
      ...(parsedTime.minute ? { minute: parsedTime.minute } : {}),
      ...(slot.score !== undefined ? { score: slot.score } : {}),
    };
  });
}

export function cadenceFieldSlotsToStrategySlots(
  slots: CadenceFieldSlot[],
): StrategyPlatformCadenceSlot[] {
  return slots.slice(0, MAX_CADENCE_SLOTS_PER_WEEK).map((slot) => ({
    weekdayIndex: cadenceDayToIndex(slot.day) ?? 0,
    timeLocal: formatTimeLocal(slot.hour, slot.minute),
    ...(slot.score !== undefined ? { score: slot.score } : {}),
  }));
}

export function platformOptimalSlotsToFieldSlots(
  slots: unknown,
): CadenceFieldSlot[] {
  if (!Array.isArray(slots)) return [];
  return slots
    .slice(0, MAX_CADENCE_SLOTS_PER_WEEK)
    .map((entry): CadenceFieldSlot | null => {
      if (!entry || typeof entry !== 'object') return null;
      const raw = entry as { day?: unknown; timeLocal?: unknown; score?: unknown };
      const weekdayIndex = cadenceDayToIndex(raw.day as string | number | undefined);
      if (weekdayIndex === null) return null;
      const parsedTime = parseTimeLocal(String(raw.timeLocal ?? ''));
      const score = Number(raw.score);
      return {
        day: cadenceIndexToDay(weekdayIndex),
        hour: parsedTime.hour,
        ...(parsedTime.minute ? { minute: parsedTime.minute } : {}),
        ...(Number.isFinite(score) ? { score } : {}),
      };
    })
    .filter((slot): slot is CadenceFieldSlot => Boolean(slot));
}

export function legacyOptimalTimesToFieldSlots(
  optimalTimes: unknown,
): CadenceFieldSlot[] {
  if (!Array.isArray(optimalTimes)) return [];
  return optimalTimes
    .slice(0, MAX_CADENCE_SLOTS_PER_WEEK)
    .map((entry): CadenceFieldSlot | null => {
      if (!entry || typeof entry !== 'object') return null;
      const raw = entry as { day?: unknown; hour?: unknown; minute?: unknown; score?: unknown };
      const weekdayIndex = cadenceDayToIndex(raw.day as string | number | undefined);
      const hour = Number(raw.hour);
      if (weekdayIndex === null || !Number.isFinite(hour)) return null;
      const minute = Number(raw.minute);
      const score = Number(raw.score);
      return {
        day: cadenceIndexToDay(weekdayIndex),
        hour,
        ...(Number.isFinite(minute) && minute > 0 ? { minute } : {}),
        ...(Number.isFinite(score) ? { score } : {}),
      };
    })
    .filter((slot): slot is CadenceFieldSlot => Boolean(slot));
}

export function cadenceFieldSlotsToPlatformOptimalSlots(
  slots: CadenceFieldSlot[],
): PlatformOptimalSlot[] {
  return slots.slice(0, MAX_CADENCE_SLOTS_PER_WEEK).map((slot) => ({
    day: slot.day,
    timeLocal: formatTimeLocal(slot.hour, slot.minute),
    ...(slot.score !== undefined ? { score: slot.score } : {}),
  }));
}

export function resolvePlatformOptimalFieldSlots(
  platform: { optimalSlots?: unknown; optimalTimes?: unknown } | undefined,
): CadenceFieldSlot[] {
  const canonical = platformOptimalSlotsToFieldSlots(platform?.optimalSlots);
  if (canonical.length > 0) return canonical;
  return legacyOptimalTimesToFieldSlots(platform?.optimalTimes);
}

export function cadenceToFieldSlots(cadence: StrategyPlatformCadence): CadenceFieldSlot[] {
  const exact = slotsToCadenceFieldSlots(cadence.slots);
  if (exact.length > 0) return exact;
  const preferredWeekdays = cadence.preferredWeekdays ?? [];
  const preferredTimesLocal = cadence.preferredTimesLocal ?? [];
  const weekdays = preferredWeekdays.length > 0
    ? preferredWeekdays
    : DEFAULT_PLATFORM_WINDOW.weekdays;
  const times = preferredTimesLocal.length > 0
    ? preferredTimesLocal
    : ['09:00'];
  const candidates = weekdays.flatMap((weekdayIndex) =>
    times.map((timeLocal) => {
      const parsedTime = parseTimeLocal(timeLocal);
      return {
        day: cadenceIndexToDay(weekdayIndex),
        hour: parsedTime.hour,
        ...(parsedTime.minute ? { minute: parsedTime.minute } : {}),
      };
    }),
  );
  const configuredCount = Number(cadence.postsPerWeek);
  const desiredCount = Number.isFinite(configuredCount) && configuredCount >= 0
    ? Math.min(MAX_CADENCE_SLOTS_PER_WEEK, Math.round(configuredCount))
    : candidates.length;
  if (desiredCount <= candidates.length) return candidates.slice(0, desiredCount);
  if (candidates.length === 0) return [];
  return Array.from({ length: desiredCount }, (_, index) => {
    const base = candidates[index % candidates.length];
    return {
      ...base,
      hour: (base.hour + Math.floor(index / candidates.length)) % 24,
    };
  });
}

export function withFieldSlots(
  cadence: StrategyPlatformCadence,
  slots: CadenceFieldSlot[],
): StrategyPlatformCadence {
  const strategySlots = cadenceFieldSlotsToStrategySlots(slots);
  const preferredWeekdays = Array.from(new Set(strategySlots.map((slot) => slot.weekdayIndex))).sort((a, b) => a - b);
  const preferredTimesLocal = Array.from(new Set(strategySlots.map((slot) => slot.timeLocal))).sort();
  return {
    ...cadence,
    postsPerWeek: strategySlots.length,
    preferredWeekdays,
    preferredTimesLocal,
    slots: strategySlots,
    preferredHourBands: undefined,
  };
}

export function postsPerWeekFromCadence(cadence: StrategyPlatformCadence): number {
  if (Array.isArray(cadence.slots)) return Math.min(MAX_CADENCE_SLOTS_PER_WEEK, cadence.slots.length);
  return cadenceToFieldSlots(cadence).length;
}

export function resolveAuthorPostsPerWeek(
  platform: AuthorPlatformLite | undefined,
  fallback: number,
): number {
  const max = Number(platform?.optimalPostFrequency?.max);
  if (Number.isFinite(max) && max > 0) return Math.round(max);
  const min = Number(platform?.optimalPostFrequency?.min);
  if (Number.isFinite(min) && min > 0) return Math.round(min);
  return fallback;
}

function slotsFromAuthorPlatform(platform: AuthorPlatformLite | undefined): StrategyPlatformCadenceSlot[] {
  return resolvePlatformOptimalFieldSlots(platform)
    .map((slot): StrategyPlatformCadenceSlot => ({
      weekdayIndex: cadenceDayToIndex(slot.day) ?? 0,
      timeLocal: formatTimeLocal(slot.hour, slot.minute),
      ...(slot.score !== undefined ? { score: slot.score } : {}),
    }));
}

function defaultSlotsForPlatform(
  _platform: PlatformId,
  postsPerWeek: number,
): StrategyPlatformCadenceSlot[] {
  const fallbackWindow = DEFAULT_PLATFORM_WINDOW;
  const count = Math.min(MAX_CADENCE_SLOTS_PER_WEEK, Math.max(0, Math.round(Number(postsPerWeek) || 0)));
  if (count === 0) return [];
  const weekdays = fallbackWindow.weekdays.length > 0 ? fallbackWindow.weekdays : [0];
  const hourBands = fallbackWindow.hourBands.length > 0 ? fallbackWindow.hourBands : [[9, 10] as [number, number]];
  const uniquePairCount = Math.max(1, weekdays.length * hourBands.length);

  return Array.from({ length: count }, (_, index) => {
    const weekdayIndex = weekdays[index % weekdays.length] ?? 0;
    const band = hourBands[Math.floor(index / weekdays.length) % hourBands.length] ?? [9, 10];
    const [start, end] = band;
    const span = Math.max(1, Math.round(end) - Math.round(start));
    const cycle = Math.floor(index / uniquePairCount);
    const hour = Math.max(0, Math.min(23, Math.round(start) + (cycle % span)));
    return {
      weekdayIndex,
      timeLocal: formatHourLocal(hour),
    };
  });
}

export function defaultStrategyCadenceForPlatform(params: {
  platform: PlatformId;
  postsPerWeek: number;
  timeZone?: string;
}): StrategyPlatformCadence {
  const fallbackWindow = DEFAULT_PLATFORM_WINDOW;
  const postsPerWeek = Math.min(
    MAX_CADENCE_SLOTS_PER_WEEK,
    Math.max(0, Math.round(Number(params.postsPerWeek) || 0)),
  );
  const slots = defaultSlotsForPlatform(params.platform, postsPerWeek);
  return {
    platform: params.platform,
    postsPerWeek,
    preferredWeekdays: Array.from(new Set(slots.map((slot) => slot.weekdayIndex))).sort((a, b) => a - b),
    preferredTimesLocal: Array.from(new Set(slots.map((slot) => slot.timeLocal))).sort(),
    slots,
    preferredHourBands: slots.length > 0
      ? undefined
      : fallbackWindow.hourBands.map(([start, end]) => ({ startHour: start, endHour: end })),
    ...(params.timeZone ? { timeZone: params.timeZone } : {}),
  };
}

export function authorPlatformToStrategyCadence(params: {
  platform: PlatformId;
  authorPlatform?: AuthorPlatformLite;
  fallbackPostsPerWeek: number;
  timeZone?: string;
}): StrategyPlatformCadence {
  const slots = slotsFromAuthorPlatform(params.authorPlatform)
    .slice(0, MAX_CADENCE_SLOTS_PER_WEEK);
  const postsPerWeek = slots.length > 0
    ? slots.length
    : Math.min(
      MAX_CADENCE_SLOTS_PER_WEEK,
      resolveAuthorPostsPerWeek(params.authorPlatform, params.fallbackPostsPerWeek),
    );
  if (slots.length === 0) {
    return defaultStrategyCadenceForPlatform({
      platform: params.platform,
      postsPerWeek,
      timeZone: params.timeZone,
    });
  }
  return {
    platform: params.platform,
    postsPerWeek,
    preferredWeekdays: Array.from(new Set(slots.map((slot) => slot.weekdayIndex))).sort((a, b) => a - b),
    preferredTimesLocal: Array.from(new Set(slots.map((slot) => slot.timeLocal))).sort(),
    slots,
    ...(params.timeZone ? { timeZone: params.timeZone } : {}),
  };
}

export function buildStrategyCadencesFromAuthor(params: {
  platforms: PlatformId[];
  authorPlatforms: AuthorPlatformLite[];
  fallbackPostsPerWeek: Record<string, number>;
  timeZone?: string;
}): StrategyPlatformCadence[] {
  return params.platforms.map((platform) => {
    const authorPlatform = params.authorPlatforms.find((candidate) => candidate.id === platform);
    return authorPlatformToStrategyCadence({
      platform,
      authorPlatform,
      fallbackPostsPerWeek: params.fallbackPostsPerWeek[platform] ?? 1,
      timeZone: params.timeZone,
    });
  });
}

export function postsPerWeekMapFromCadences(
  cadences: StrategyPlatformCadence[],
): Partial<Record<PlatformId, number>> {
  return cadences.reduce<Partial<Record<PlatformId, number>>>((acc, cadence) => {
    acc[cadence.platform] = postsPerWeekFromCadence(cadence);
    return acc;
  }, {});
}
